import React from 'react';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { useIntl } from 'react-intl';
import { reportError } from 'utils/sentry.utils';
import { useMount } from 'ahooks';
import isString from 'lodash/isString';
import { ReactComponent as SomethingWentWrongSVG } from 'assets/images/something-went-wrong.svg';
import { Illustration } from 'components/common/illustration';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';

interface Props {
  errorExtraInfo?: Record<string, unknown> & { reason?: string };
  errorToReport?: any; // give this prop only if you want to report it to Sentry
  message?: string;
  messageValues?: Record<string, string>;
  icon?: React.ReactElement;
  className?: string;
}

const unexpectedErrorId = 'notifications.error.something.went.wrong';

const ErrorContainer: React.FunctionComponent<Props> = ({
  errorToReport,
  errorExtraInfo,
  message = unexpectedErrorId,
  messageValues,
  icon,
  className
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const translatedMessage = message && intl.formatMessage({ id: message, defaultMessage: message }, messageValues);

  useMount(() => {
    if (errorToReport) {
      reportError('ErrorContainer', errorToReport, {
        path: window.location.href,
        message: translatedMessage,
        ...errorExtraInfo
      });
    }
  });

  const defaultArt = theme.isNewDesign ? (
    <Illustration name="something-went-wrong" data-e2e="error-container-illustration" />
  ) : (
    <StyledSomethingWentWrongIcon />
  );

  return (
    <Container data-e2e="error-container" className={className}>
      {icon || defaultArt}
      <EmptyLayoutMessage data-e2e="error-container-actual-msg">{translatedMessage}</EmptyLayoutMessage>
      {isString(errorExtraInfo?.reason) && <Reason>{errorExtraInfo!.reason}</Reason>}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100% - 46px);
  width: 100%;

  ${legacyOnly(
    theme => css`
      color: ${theme.textGray};
    `
  )}

  ${newDesignOnly(
    theme => css`
      gap: ${theme.spacing.xs};
      color: var(--text-body);
    `
  )}
`;

// legacyOnly leads so the switch-off rule keeps the pre-restyle declaration order; the parity
// fixture is a byte comparison.
const EmptyLayoutMessage = styled.h2`
  ${legacyOnly(css`
    font-size: 24px;
  `)}

  white-space: pre-line;

  ${newDesignOnly(css`
    ${typeStyle.headingH4}
    color: var(--text-heading);
    margin: 0;
  `)}
`;

const Reason = styled.span`
  ${newDesignOnly(css`
    ${typeStyle.bodyMd}
    color: var(--text-secondary);
  `)}
`;

const StyledSomethingWentWrongIcon = styled(SomethingWentWrongSVG)`
  width: 200px;
  height: 200px;
`;

export default ErrorContainer;
